import { useEffect, useState } from "react";
import { toast } from "sonner";
import { ListChecks, ChevronDown, Plus, Pencil, Trash2, X } from "lucide-react";
import { api, formatApiErrorDetail } from "../../lib/api";
import { inputCls, btnCls } from "./crud";

const label = (t) => <label className="font-mono-tech text-[10px] tracking-widest text-slate-500 block mb-1">{t}</label>;

function ValueRow({ value, onChange, onDelete }) {
  const [text, setText] = useState(value);
  useEffect(() => { setText(value); }, [value]);
  return (
    <div className="flex items-center gap-2">
      <input className={inputCls} value={text} onChange={(e) => setText(e.target.value)}
        onBlur={() => { if (text.trim() && text !== value) onChange(text.trim()); else setText(value); }}
        data-testid="value-row-input" />
      <button onClick={onDelete} className="shrink-0 w-8 h-8 border border-slate-200 rounded-md flex items-center justify-center text-slate-400 hover:text-red-600 hover:border-red-300 transition-colors duration-200" data-testid="value-row-delete">
        <Trash2 size={13} />
      </button>
    </div>
  );
}

function CatalogCard({ item, onSave, onEditMeta, onDeleteCatalog }) {
  const [newVal, setNewVal] = useState("");
  const values = item.values || [];
  const update = (vals) => onSave(item, vals);
  const add = () => {
    const v = newVal.trim();
    if (!v) return;
    if (values.includes(v)) { toast.error("Ese valor ya existe en el catálogo"); return; }
    update([...values, v]); setNewVal("");
  };
  return (
    <div className="border border-slate-200 rounded-xl bg-white p-5" data-testid={`catalog-card-${item.key}`}>
      <div className="flex items-start justify-between gap-3 mb-1">
        <div className="flex items-center gap-2">
          <ListChecks size={15} className="text-[#5698C7] shrink-0" />
          <h3 className="font-display text-[13px] font-semibold text-[#0B1220]">{item.label || item.key}</h3>
        </div>
        <div className="flex gap-2 shrink-0">
          <button onClick={() => onEditMeta(item)} className="text-slate-400 hover:text-[#5698C7] transition-colors duration-200" data-testid={`catalog-edit-${item.key}`}><Pencil size={13} /></button>
          <button onClick={() => onDeleteCatalog(item)} className="text-slate-400 hover:text-red-600 transition-colors duration-200" data-testid={`catalog-delete-${item.key}`}><Trash2 size={13} /></button>
        </div>
      </div>
      <p className="text-[11px] text-slate-400 mb-1">{item.description || ""}</p>
      <span className="font-mono-tech text-[9px] tracking-widest text-slate-300 block mb-3">{item.key}</span>
      <div className="space-y-2">
        {values.map((v, i) => (
          <ValueRow key={`${v}-${i}`} value={v}
            onChange={(nv) => update(values.map((x, xi) => (xi === i ? nv : x)))}
            onDelete={() => { if (window.confirm(`¿Eliminar el valor "${v}"?`)) update(values.filter((_, xi) => xi !== i)); }} />
        ))}
        <div className="flex items-center gap-2 pt-1">
          <input className={inputCls} placeholder="Nuevo valor..." value={newVal} onChange={(e) => setNewVal(e.target.value)}
            onKeyDown={(e) => e.key === "Enter" && add()} data-testid={`catalog-new-value-${item.key}`} />
          <button onClick={add} disabled={!newVal.trim()} className={btnCls + " shrink-0"} data-testid={`catalog-add-value-${item.key}`}>
            <Plus size={13} /> Añadir
          </button>
        </div>
      </div>
    </div>
  );
}

function CatalogForm({ initial, onClose, onSaved }) {
  const [m, setM] = useState(initial);
  const set = (k) => (e) => setM({ ...m, [k]: e.target.value });
  const save = async () => {
    if (!m.key.trim()) { toast.error("La clave técnica es obligatoria"); return; }
    try {
      const { _id, created_at, updated_at, ...rest } = m;
      const data = { ...rest, key: m.key.trim().toLowerCase().replace(/\s+/g, "_") };
      if (_id) await api.put(`/admin/content/settings/${_id}`, { data });
      else await api.post("/admin/content/settings", { data: { ...data, values: [] } });
      toast.success("Guardado"); onSaved();
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
  };
  return (
    <div className="fixed inset-0 z-50 bg-slate-900/40 flex items-center justify-center p-4">
      <div className="bg-white rounded-xl shadow-xl border border-slate-200 w-full max-w-md p-6" data-testid="catalog-form-modal">
        <div className="flex items-center justify-between mb-6">
          <h3 className="font-display text-sm font-semibold">{m._id ? "Editar catálogo" : "Nuevo catálogo"}</h3>
          <button onClick={onClose} data-testid="catalog-form-close"><X size={16} className="text-slate-500" /></button>
        </div>
        <div className="space-y-4">
          <div>{label("TÍTULO")}<input className={inputCls} placeholder="ej: Contactos · Método" value={m.label || ""} onChange={set("label")} data-testid="catalog-field-label" /></div>
          <div>{label("DESCRIPCIÓN")}<input className={inputCls} value={m.description || ""} onChange={set("description")} data-testid="catalog-field-description" /></div>
          <div>{label("CLAVE TÉCNICA")}
            <input className={inputCls} placeholder="ej: contact_methods" value={m.key} onChange={set("key")} disabled={!!m._id} data-testid="catalog-field-key" />
            {m._id && <span className="block mt-1 text-[10px] text-slate-400">La clave no se puede cambiar: los formularios la referencian.</span>}
          </div>
        </div>
        <div className="mt-6 flex justify-end"><button onClick={save} className={btnCls} data-testid="catalog-save-btn">GUARDAR</button></div>
      </div>
    </div>
  );
}

export default function ValueLists() {
  const [items, setItems] = useState([]);
  const [open, setOpen] = useState(true);
  const [editing, setEditing] = useState(null);

  const load = () => api.get("/admin/content/settings").then((r) => setItems(r.data.filter((s) => s.key !== "recaptcha"))).catch(() => {});
  useEffect(() => { load(); }, []);

  const saveValues = async (item, values) => {
    const prev = items;
    setItems(items.map((i) => (i._id === item._id ? { ...i, values } : i)));
    try {
      const { _id, created_at, updated_at, ...rest } = item;
      await api.put(`/admin/content/settings/${item._id}`, { data: { ...rest, values } });
    } catch (e) { setItems(prev); toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
  };

  const removeCatalog = async (item) => {
    if (!window.confirm(`¿Eliminar el catálogo completo "${item.label || item.key}"? Los desplegables que lo usan quedarán vacíos.`)) return;
    await api.delete(`/admin/content/settings/${item._id}`);
    toast.success("Catálogo eliminado"); load();
  };

  return (
    <div className="border border-slate-200 rounded-lg bg-white shadow-sm mb-10" data-testid="value-lists-section">
      <div className="w-full flex items-center gap-3 px-6 py-4">
        <button onClick={() => setOpen(!open)} className="flex items-center gap-3 flex-1 text-left" data-testid="value-lists-toggle">
          <ChevronDown size={16} className={`text-slate-400 transition-transform duration-200 ${open ? "" : "-rotate-90"}`} />
          <div>
            <span className="font-display text-sm font-medium text-[#0B1220]">Listado de valores</span>
            <span className="block text-[11px] text-slate-400">Fuentes centralizadas para los desplegables de la aplicación · Nada de texto libre para clasificaciones</span>
          </div>
        </button>
        <button onClick={() => setEditing({ key: "", label: "", description: "" })} className={btnCls} data-testid="catalog-add-btn"><Plus size={14} /> AÑADIR CATÁLOGO</button>
      </div>
      {open && (
        <div className="px-6 pb-6 grid grid-cols-1 lg:grid-cols-2 gap-4">
          {items.map((item) => (
            <CatalogCard key={item._id} item={item} onSave={saveValues} onEditMeta={(i) => setEditing({ ...i })} onDeleteCatalog={removeCatalog} />
          ))}
          {items.length === 0 && <p className="text-xs text-slate-400">Sin catálogos.</p>}
        </div>
      )}
      {editing && <CatalogForm initial={editing} onClose={() => setEditing(null)} onSaved={() => { setEditing(null); load(); }} />}
    </div>
  );
}
